"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Heart } from "lucide-react";
import { Header, MobileBottomNav, Footer } from "@/components/layout";
import { PUBLIC_LISTING_FIELDS } from "@/lib/db/public-fields";
import { ListingCard } from "@/components/common";
import { useFavorites } from "@/hooks";
import { createClient } from "@/lib/supabase/client";
import { transformListings } from "@/lib/utils/transform";
import type { Listing } from "@/types";

export default function FavoritesPage() {
  const { favorites, isLoaded, isFavorite, toggleFavorite } = useFavorites();
  const [favoriteListings, setFavoriteListings] = useState<Listing[]>([]);
  const [isLoadingListings, setIsLoadingListings] = useState(false);

  // Favori ID'leri değiştiğinde veritabanından ilanları çek
  useEffect(() => {
    async function fetchFavoriteListings() {
      if (!isLoaded || favorites.length === 0) {
        setFavoriteListings([]);
        return;
      }

      setIsLoadingListings(true);
      try {
        const supabase = createClient();
        if (!supabase) {
          console.error("Supabase client oluşturulamadı");
          setFavoriteListings([]);
          return;
        }

        const { data, error } = await supabase
          .from("listings")
          // Bu sorgu TARAYICIDA çalışıyor ve BAŞKA ev sahiplerinin ilanlarını
          // çekiyor — `*` kullanılamaz (gerekçe: lib/db/public-fields.ts).
          .select(PUBLIC_LISTING_FIELDS)
          .in("id", favorites)
          .eq("status", "active");

        if (error) {
          console.error("Favori ilanları çekilemedi:", error);
          setFavoriteListings([]);
          return;
        }

        const listings = transformListings(data || []);
        setFavoriteListings(listings);
      } catch (error) {
        console.error("Favori ilanları çekilemedi:", error);
        setFavoriteListings([]);
      } finally {
        setIsLoadingListings(false);
      }
    }

    fetchFavoriteListings();
  }, [favorites, isLoaded]);

  const isLoading = !isLoaded || isLoadingListings;

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />

      <main className="flex-1 pb-20 md:pb-0">
        <div className="mx-auto max-w-4xl px-4 py-8 md:px-6 md:py-12">
          {/* Başlık */}
          <h1 className="text-foreground text-2xl font-bold md:text-3xl">Favorileriniz</h1>

          {/* Loading State */}
          {isLoading ? (
            <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-3">
                  <div className="bg-muted aspect-[4/3] animate-pulse rounded-xl" />
                  <div className="space-y-2">
                    <div className="bg-muted h-3 w-24 animate-pulse rounded" />
                    <div className="bg-muted h-5 w-3/4 animate-pulse rounded" />
                    <div className="bg-muted h-4 w-1/3 animate-pulse rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : favorites.length === 0 ? (
            /* Boş Durum - Hiç favori eklenmemiş */
            <div className="mt-16 flex flex-col items-center justify-center text-center">
              <div className="bg-muted flex h-20 w-20 items-center justify-center rounded-full">
                <Heart className="text-muted-foreground h-10 w-10" />
              </div>
              <h2 className="text-foreground mt-6 text-xl font-semibold">
                Henüz favori eklemediniz
              </h2>
              <p className="text-muted-foreground mt-2 max-w-sm">
                Beğendiğiniz ilanları favorilere ekleyerek daha sonra kolayca ulaşabilirsiniz.
              </p>
              <Link
                href="/"
                className="bg-primary hover:bg-primary/90 mt-6 inline-flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors"
              >
                Hemen Keşfet
              </Link>
            </div>
          ) : favoriteListings.length === 0 ? (
            /* Favori var ama ilanlar bulunamadı (silinmiş veya pasif) */
            <div className="mt-16 flex flex-col items-center justify-center text-center">
              <div className="bg-muted flex h-20 w-20 items-center justify-center rounded-full">
                <Heart className="text-muted-foreground h-10 w-10" />
              </div>
              <h2 className="text-foreground mt-6 text-xl font-semibold">
                Favori ilanlar bulunamadı
              </h2>
              <p className="text-muted-foreground mt-2 max-w-sm">
                Favorilediğiniz ilanlar artık aktif değil veya kaldırılmış olabilir.
              </p>
              <Link
                href="/"
                className="bg-primary hover:bg-primary/90 mt-6 inline-flex items-center gap-2 rounded-lg px-6 py-3 font-medium text-white transition-colors"
              >
                Yeni İlanları Keşfet
              </Link>
            </div>
          ) : (
            /* Favori Listesi */
            <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-3">
              {favoriteListings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  isFavorite={isFavorite(listing.id)}
                  onFavoriteClick={toggleFavorite}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <MobileBottomNav />
    </div>
  );
}
